import type {
  EffectiveFlagPolicy,
  FlagAmountThresholdInput,
  FlagPriority,
  FlagTypePolicyInput,
} from '@generated/graphql';
import { FlagTypes } from '@generated/graphql';
import type {
  FlagPolicyEnabledState,
  FlagPolicyRowView,
} from '../flagPolicies.types';

export type FlagAmountThresholdAxis = 'CENTS' | 'PERCENT_ONLY';

export const FLAG_AMOUNT_THRESHOLD_AXIS: Partial<
  Record<FlagTypes, FlagAmountThresholdAxis>
> = {
  [FlagTypes.MoneyAvailableValueIncorrectV2]: 'CENTS',
  [FlagTypes.PendingTransactionMoneyAvailableValueIncorrect]: 'CENTS',
  [FlagTypes.FeeAmountDifferent]: 'CENTS',
  [FlagTypes.DifferentBookvalue]: 'CENTS',
  [FlagTypes.RiskyDifferentHoldingUsdEtf]: 'CENTS',
  [FlagTypes.DifferentHoldingsV2]: 'PERCENT_ONLY',
  [FlagTypes.PendingTransactionDifferentHoldings]: 'PERCENT_ONLY',
  [FlagTypes.RiskyDifferentHoldingMfSellAll]: 'PERCENT_ONLY',
};

export const FLAG_AMOUNT_THRESHOLDABLE_TYPES = Object.keys(
  FLAG_AMOUNT_THRESHOLD_AXIS,
) as FlagTypes[];

export const isThresholdableFlagType = (
  flagType: string,
): flagType is FlagTypes => flagType in FLAG_AMOUNT_THRESHOLD_AXIS;

export const getThresholdAxis = (
  flagType: string,
): FlagAmountThresholdAxis | null =>
  isThresholdableFlagType(flagType)
    ? (FLAG_AMOUNT_THRESHOLD_AXIS[flagType] ?? null)
    : null;

export const EDITABLE_FLAG_PRIORITIES = [
  'CRITICAL',
  'HIGH',
  'MEDIUM',
  'LOW',
] as const;

/** Display/API: integer cents → dollar string for settings inputs (e.g. 5 → "0.05"). */
export const formatThresholdCentsAsDollars = (cents: number): string =>
  (cents / 100).toFixed(2);

/** Settings input: dollar string → integer cents for API (e.g. "0.05" → 5, "5.00" → 500). */
export const parseThresholdDollarsToCents = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  const dollars = Number(trimmed);
  if (Number.isNaN(dollars)) {
    return null;
  }
  return Math.round(dollars * 100);
};

export type EditableFlagPriority = (typeof EDITABLE_FLAG_PRIORITIES)[number];

export const findPolicyOverride = (
  rows: FlagTypePolicyInput[],
  flagType: FlagTypes,
): FlagTypePolicyInput | undefined =>
  rows.find((row) => row.flagType === flagType);

export const findThresholdOverride = (
  rows: FlagAmountThresholdInput[],
  flagType: FlagTypes,
): FlagAmountThresholdInput | undefined =>
  rows.find((row) => row.flagType === flagType);

export const getEnabledControlState = (
  policyOverride: FlagTypePolicyInput | undefined,
): FlagPolicyEnabledState => {
  if (policyOverride?.enabled == null) {
    return 'UNDEFINED';
  }
  return policyOverride.enabled ? 'ON' : 'OFF';
};

type BuildPolicyOverrideForEnabledStateInput = {
  flagType: FlagTypes;
  state: FlagPolicyEnabledState;
  existing?: FlagTypePolicyInput;
  initialPolicy?: FlagTypePolicyInput;
  effective?: Pick<EffectiveFlagPolicy, 'priority'>;
};

/** Builds the policy override row for a Yes/No/Inherited segment change. */
export const buildPolicyOverrideForEnabledState = ({
  flagType,
  state,
  existing,
  initialPolicy,
  effective,
}: BuildPolicyOverrideForEnabledStateInput): FlagTypePolicyInput | null => {
  if (state === 'UNDEFINED') {
    return null;
  }

  if (state === 'OFF') {
    const override: FlagTypePolicyInput = { flagType, enabled: false };
    const priority = existing?.priority ?? initialPolicy?.priority;
    if (priority != null) {
      override.priority = priority;
    }
    return override;
  }

  const override: FlagTypePolicyInput = { flagType, enabled: true };
  const priority = existing?.priority ?? initialPolicy?.priority;
  const hadExplicitPriority = initialPolicy?.priority != null;
  if (
    priority != null &&
    (hadExplicitPriority || priority !== effective?.priority)
  ) {
    override.priority = priority;
  }
  return override;
};

type ShouldSyncOrgPolicyFromQueryInput = {
  activeOrganizationId: string;
  fetchedOrganizationId: string | undefined;
  syncedOrganizationId: string | undefined;
  isDirty: boolean;
};

/** Whether query data should replace local draft/baseline override state. */
export const shouldSyncOrgPolicyFromQuery = ({
  activeOrganizationId,
  fetchedOrganizationId,
  syncedOrganizationId,
  isDirty,
}: ShouldSyncOrgPolicyFromQueryInput): boolean => {
  if (
    !fetchedOrganizationId ||
    fetchedOrganizationId !== activeOrganizationId
  ) {
    return false;
  }
  if (isDirty) {
    return false;
  }
  if (syncedOrganizationId === activeOrganizationId) {
    return false;
  }
  return true;
};

/** Re-applies a saved threshold override when re-enabling a flag after Inherited. */
export const restoreInitialThresholdOnEnable = (
  currentThresholds: FlagAmountThresholdInput[],
  initialThresholds: FlagAmountThresholdInput[],
  flagType: FlagTypes,
): FlagAmountThresholdInput[] => {
  if (findThresholdOverride(currentThresholds, flagType)) {
    return currentThresholds;
  }

  const initialThreshold = findThresholdOverride(initialThresholds, flagType);
  if (!initialThreshold) {
    return currentThresholds;
  }

  return [...currentThresholds, initialThreshold];
};

export const buildFlagPolicyRowView = (
  effective: EffectiveFlagPolicy,
  flagTypePolicies: FlagTypePolicyInput[],
  flagAmountThresholds: FlagAmountThresholdInput[],
  initialPolicies: FlagTypePolicyInput[],
): FlagPolicyRowView => {
  const policyOverride = findPolicyOverride(
    flagTypePolicies,
    effective.flagType,
  );
  const initialOverride = findPolicyOverride(
    initialPolicies,
    effective.flagType,
  );
  const thresholdOverride = findThresholdOverride(
    flagAmountThresholds,
    effective.flagType,
  );

  const enabledControlState = getEnabledControlState(policyOverride);
  const originalEnabledControlState = getEnabledControlState(initialOverride);
  const hasEnabledOverride = policyOverride?.enabled != null;

  return {
    flagType: effective.flagType,
    enabled:
      enabledControlState === 'UNDEFINED'
        ? effective.enabled
        : enabledControlState === 'ON',
    priority: (policyOverride?.priority ?? effective.priority) as FlagPriority,
    thresholdCents:
      thresholdOverride?.thresholdCents ?? effective.thresholdCents ?? null,
    percentThreshold:
      thresholdOverride?.percentThreshold ?? effective.percentThreshold ?? null,
    effective,
    hasPolicyOverride: policyOverride != null,
    hasThresholdOverride: thresholdOverride != null,
    hasEnabledOverride,
    isCustomized: policyOverride != null || thresholdOverride != null,
    enabledControlState,
    originalEnabledControlState,
  };
};
